'use client'

import useEmblaCarousel from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

import { cn } from '@/utilities/ui'

type Props = {
  children: React.ReactNode[]
  className?: string
  slideClassName?: string
  autoplayMs?: number
  /** Setas de navegação (canto superior direito). */
  setas?: boolean
  pontos?: boolean
  loop?: boolean
  rotuloAnterior?: string
  rotuloProximo?: string
}

/** Carrossel Embla com arrasto, setas, pontos e autoplay opcional (pausa no hover). */
export function CarrosselAgencia({
  children,
  className,
  slideClassName = 'min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3',
  autoplayMs,
  setas = true,
  pontos = true,
  loop = true,
  rotuloAnterior = 'Anterior',
  rotuloProximo = 'Próximo',
}: Props) {
  const [ref, api] = useEmblaCarousel({ loop, align: 'start', dragFree: false })
  const [index, setIndex] = useState(0)
  const [hover, setHover] = useState(false)
  const total = children.length

  useEffect(() => {
    if (!api) return
    const onSelect = () => setIndex(api.selectedScrollSnap())
    api.on('select', onSelect)
    onSelect()
    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  useEffect(() => {
    if (!api || !autoplayMs || hover) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = setInterval(() => api.scrollNext(), autoplayMs)
    return () => clearInterval(t)
  }, [api, autoplayMs, hover])

  const prev = useCallback(() => api?.scrollPrev(), [api])
  const next = useCallback(() => api?.scrollNext(), [api])

  return (
    <div
      className={cn('relative', className)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="region"
      aria-roledescription="carrossel"
    >
      <div ref={ref} className="overflow-hidden">
        <div className="-ml-6 flex touch-pan-y">
          {children.map((c, i) => (
            <div key={i} className={cn('pl-6', slideClassName)} aria-roledescription="slide" aria-label={`${i + 1} de ${total}`}>
              {c}
            </div>
          ))}
        </div>
      </div>
      {(setas || pontos) && total > 1 ? (
        <div className="mt-8 flex items-center justify-between">
          {pontos ? (
            <div className="flex gap-2" aria-hidden>
              {children.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => api?.scrollTo(i)}
                  className={cn('h-1.5 w-6 transition-colors', i === index ? 'bg-[var(--sec-fg)]' : 'bg-[rgb(var(--sec-fg-rgb)/0.2)]')}
                  tabIndex={-1}
                />
              ))}
            </div>
          ) : (
            <span />
          )}
          {setas ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={prev}
                aria-label={rotuloAnterior}
                className="flex h-10 w-10 items-center justify-center border-[0.5px] border-[rgb(var(--sec-border-rgb)/0.3)] transition-colors hover:bg-[rgb(var(--sec-fg-rgb)/0.06)]"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label={rotuloProximo}
                className="flex h-10 w-10 items-center justify-center border-[0.5px] border-[rgb(var(--sec-border-rgb)/0.3)] transition-colors hover:bg-[rgb(var(--sec-fg-rgb)/0.06)]"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
